import { Box, Flex, Icon, Text, VisuallyHidden } from '@chakra-ui/react'
import { Building2, ChevronDown, ChevronUp } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMenuStyles } from '~/components/shared/componentStyles/Menu.style'
import { MenuPositioningWrapper } from '~/components/shared/Dropdown/Menu'
import { PbxPicker, usePbxSwitcher } from '~/components/shared/PbxPicker'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { useGetPbxList } from '~/queries/pbxQueries'
import { ZIndex } from '~/types/ThemeTypes'
import { castRef } from '~/utils/refUtils'

import { keyboardFocusRing } from '@mangovoice/design-system'

const POPOVER_WIDTH = '20rem'

export const PbxHeaderSwitcher = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverTitleId = useId()

  const { data: pbxListData } = useGetPbxList({
    apiParams: { limit: 1 },
  })
  const hasMultiplePbxs = (pbxListData?.count ?? 0) > 1

  // Hook is called at the component level so the underlying state survives
  // the popover close — the user-confirm callback fires after the popover
  // unmounts its content, and would otherwise hit a stale/no-op setState.
  const { currentPbx, handleSelect } = usePbxSwitcher({
    onAfterSwitchInitiated: () => setIsOpen(false),
  })

  const handleOutsideClick = useCallback(() => setIsOpen(false), [])

  useOutsideClick({
    ref: castRef(containerRef),
    handler: handleOutsideClick,
    enabled: isOpen,
  })

  useEffect(() => {
    if (!isOpen) return
    // Window capture runs before zag's document-capture Escape listener
    // (Admin drawer), so the drawer neither sees nor dismisses on this key.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // A mouse click on non-focusable popover content leaves focus on body.
      const target = e.target as Node
      if (target !== document.body && !containerRef.current?.contains(target))
        return
      e.preventDefault()
      e.stopPropagation()
      setIsOpen(false)
      triggerRef.current?.focus()
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen])

  const menuStyles = useMenuStyles()

  if (!hasMultiplePbxs || !currentPbx) return null

  return (
    <MenuPositioningWrapper ref={containerRef}>
      <Flex
        asChild
        align="center"
        gap="2"
        h="2.5rem"
        px="4"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.default"
        bg="bg.panel"
        cursor="pointer"
        transition="background-color 0.15s"
        _hover={{ bg: 'bg.muted' }}
        {...keyboardFocusRing}
      >
        <button
          type="button"
          ref={triggerRef}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          data-testid="pbx-header-switcher-trigger"
        >
          <VisuallyHidden>
            {t('pbxHeaderSwitcher.ariaLabel', 'Switch PBX')}
          </VisuallyHidden>
          <Icon as={Building2} boxSize="4" color="fg.subtle" aria-hidden />
          <Text
            as="span"
            fontSize="sm"
            fontWeight="500"
            color="fg.default"
            maxWidth="10rem"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {currentPbx.name}
          </Text>
          <Icon
            as={isOpen ? ChevronUp : ChevronDown}
            boxSize="4"
            color="fg.subtle"
            aria-hidden
          />
        </button>
      </Flex>

      {isOpen && (
        <Box
          css={menuStyles.container}
          position="absolute"
          top="calc(100% + 0.375rem)"
          right="0"
          zIndex={ZIndex.popover}
          width={POPOVER_WIDTH}
          padding="3"
          role="dialog"
          aria-labelledby={popoverTitleId}
          data-testid="pbx-header-switcher-popover"
        >
          <Text
            id={popoverTitleId}
            fontSize="xs"
            fontWeight="600"
            color="fg.subtle"
            textTransform="uppercase"
            letterSpacing="wider"
            mb="2"
            px="1"
          >
            {t('pbxHeaderSwitcher.selectPbx', 'Select PBX')}
          </Text>
          <PbxPicker
            currentPbx={currentPbx}
            onSelect={handleSelect}
            listLabelledBy={popoverTitleId}
          />
        </Box>
      )}
    </MenuPositioningWrapper>
  )
}
